import axios from 'axios';
import { clearCache } from './userContext';
import { getBongoUrl } from '../common/utils/bongo';
import { getAccessApiUrl } from '../common/utils/accessApi';

/**
 * Generate random state parameter for OAuth CSRF protection
 *
 * Implements OAuth 2.0 state parameter as defined in RFC 6749 Section 10.12
 * and OAuth 2.0 Security Best Current Practice (RFC 6819).
 *
 * Security considerations:
 * - Uses cryptographically secure random generation (Web Crypto API)
 * - Provides 128 bits of entropy (16 bytes = 2^128 possible values)
 * - State is validated on callback to prevent CSRF attacks
 *
 * References:
 * - RFC 6749: https://datatracker.ietf.org/doc/html/rfc6749#section-10.12
 * - OAuth 2.0 Security Best Practices: https://oauth.net/2/oauth-best-practice/
 */
export function generateState(): string {
  const array = new Uint8Array(16);
  window.crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join(
    ''
  );
}

/**
 * Initiate OAuth authorization flow
 * Redirects user to mk-auth service for authentication
 * Destination route should be stored in sessionStorage as 'oauth_redirect' before calling this
 */
export function initiateOAuthFlow(): void {
  // Prevent multiple simultaneous redirects
  if (sessionStorage.getItem('oauth_redirecting') === 'true') {
    return;
  }

  const state = generateState();
  const currentAppUrl = window.location.origin; // e.g., https://app.hginsights.com

  // Store state in sessionStorage
  sessionStorage.setItem('oauth_state', state);
  sessionStorage.setItem('oauth_redirecting', 'true'); // Flag to prevent multiple redirects

  const authUrl =
    `${getAccessApiUrl()}/oauth/authorize?` +
    `client_id=${OAUTH_CLIENT_ID}&` +
    `redirect_uri=${encodeURIComponent(`${currentAppUrl}/oauth/callback`)}&` +
    `response_type=code&` +
    `scope=openid profile email&` +
    `state=${state}`;

  window.location.href = authUrl;
}

/**
 * Exchange OAuth authorization code for tokens
 * Validates state parameter and exchanges code via backend
 */
export async function exchangeOAuthCode(): Promise<void> {
  // Clear redirecting flag
  sessionStorage.removeItem('oauth_redirecting');

  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get('code');
  const state = urlParams.get('state');
  const error = urlParams.get('error');
  const errorDescription = urlParams.get('error_description');

  // Handle OAuth errors
  if (error) {
    console.error('OAuth error:', error, errorDescription || 'Unknown error');

    // Clear OAuth state to prevent retry
    sessionStorage.removeItem('oauth_state');
    sessionStorage.removeItem('oauth_redirect');

    // Redirect to home - user can try again
    window.location.href = '/';
    throw new Error(`OAuth error: ${error} - ${errorDescription}`);
  }

  // Validate state
  const storedState = sessionStorage.getItem('oauth_state');
  if (!state || !code || state !== storedState) {
    console.error('Invalid OAuth state or missing code');
    sessionStorage.removeItem('oauth_state');
    sessionStorage.removeItem('oauth_redirect');
    // Redirect to home - user can try again
    window.location.href = '/';
    throw new Error('Invalid OAuth state or missing code');
  }

  try {
    // Exchange code for tokens
    await axios.post(
      `${getBongoUrl()}/auth/mk-auth/exchange`,
      {
        code,
        redirectUri: `${window.location.origin}/oauth/callback`,
      },
      {
        withCredentials: true,
      }
    );

    // Clear OAuth state
    sessionStorage.removeItem('oauth_state');
  } catch (exchangeError) {
    console.error('Token exchange failed:', exchangeError);
    sessionStorage.removeItem('oauth_state');
    sessionStorage.removeItem('oauth_redirect');
    // Redirect to home - user can try again
    window.location.href = '/';
    throw exchangeError;
  }
}

/**
 * Refresh authentication token
 *
 * This function is called automatically by getCurrentUser() and getCurrentTenant()
 * when a 401 Unauthorized error is detected, before redirecting to OAuth login.
 *
 * Flow:
 * 1. Frontend sends refresh_token cookie (httpOnly) to backend
 * 2. Backend exchanges refresh_token for new tokens via mk-auth
 * 3. Backend returns new tokens in Set-Cookie headers
 * 4. Browser automatically updates cookies
 *
 * If refresh fails, the user will be redirected to OAuth login flow.
 *
 * @throws {Error} If refresh fails (token expired, invalid, etc.)
 */
export async function refreshToken(): Promise<void> {
  await axios.post(
    `${getBongoUrl()}/auth/mk-auth/refresh`,
    {},
    { withCredentials: true }
  );
}

/**
 * Logout user - revoke tokens and clear session
 */
export async function logout(): Promise<void> {
  try {
    await axios.post(
      `${getBongoUrl()}/auth/mk-auth/logout`,
      {},
      { withCredentials: true }
    );
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    clearCache(); // Clear user/tenant cache
    sessionStorage.clear(); // Clean up OAuth state
  }
}
